import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Method, Prop } from '@stencil/core';
import { isKeyboardClickOnFocusedElement } from '../../../utils/is-keyboard-click-on-focused-element';
import { transformTagNames } from '../../../global/transform-tag-name';

/**
 * @slot - text content or optional wb-counter element
 */
@Component({
  tag: 'wb-tab',
  styleUrl: './tab.scss',
  shadow: true,
})
export class TabComponent implements ComponentInterface {
  @Element()
  public el!: HTMLWbTabElement;

  /** Name of the tab */
  @Prop({ reflect: true })
  public name!: string;

  /** If present, shows the tab as disabled */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** If present, hides the text by setting the font-size to 0 (for a11y) */
  @Prop({ reflect: true })
  public iconOnly: boolean = false;

  /** @internal apply to render element, set by wb-tabs */
  @Prop({ reflect: true })
  public selected: boolean = false;

  private TagNames = transformTagNames(this, 'wb-tab', ['wb-text', 'wb-tabs']);

  private tabsEl: HTMLWbTabsElement;

  /** @internal called by parent <wb-tabs> to enforce propagation of theme changes */
  @Method()
  public async updateTheme(): Promise<void> {
    this.inheritAttributes();
  }

  @Listen('keydown')
  public handleKeyDown(ev: KeyboardEvent): void {
    if (isKeyboardClickOnFocusedElement(this.el, ev)) {
      ev.preventDefault();
      this.el.click();
    }
  }

  public connectedCallback(): void {
    this.tabsEl = this.el.closest<HTMLWbTabsElement>(this.TagNames.WbTabs);
  }

  public componentWillLoad(): void {
    this.inheritAttributes();
  }

  private inheritAttributes(): void {
    this.el.setAttribute('theme', this.tabsEl.theme);
  }

  private updateTabsHighlight = (): void => {
    this.tabsEl.updateTabsHighlight();
  };

  public render(): JSX.Element {
    const { WbText } = this.TagNames;

    return (
      <Host class="wb-tab" role="tab" tabindex={this.disabled ? null : '0'} aria-selected={String(this.selected)}>
        <WbText size="l" static strong>
          <div class="text">
            <slot onSlotchange={this.updateTabsHighlight} />
          </div>
        </WbText>
      </Host>
    );
  }
}
